/**
 * Qué hacer con el usuario que cambia de empresa estando dentro del módulo de nóminas.
 *
 * El selector de empresa de la cabecera cambia la empresa **sin cambiar la URL**, así
 * que quien estaba en `/81241/nominas` (una empresa A3) y salta a una Reviso se queda
 * mirando el listado de la anterior —con sus nóminas, sus contadores y su papelera—
 * mientras la cabecera ya dice otra empresa. Pasó en real.
 *
 * Se resuelve aquí, como función pura, y no dentro de la acción del store: esa acción
 * arrastra media aplicación (permisos, zonas, filtros, conectores) y no hay forma de
 * probarla; la decisión sí, y es la parte que puede equivocarse.
 */

/** Las pantallas del módulo. Desde cualquiera se puede cambiar de empresa. */
const NOMINA_ROUTE_NAME_LIST = ['nominas', 'nomina-detail', 'nominas-papelera', 'nominas-configuracion'];

const NOT_AVAILABLE_ROUTE_NAME = 'nominas-not-available';
const LIST_ROUTE_NAME = 'nominas';

export interface NominaCompanyChangeInput {
  routeName: string;
  companyId: number | string;
  hasNominaFeature: boolean;
}

export interface NominaCompanyChangeRoute {
  name: string;
  params: { companydb_id: string };
}

export function resolveNominaCompanyChangeRoute(
  input: NominaCompanyChangeInput,
): NominaCompanyChangeRoute | null {
  const companyParams = { companydb_id: String(input.companyId) };
  const isInModule = NOMINA_ROUTE_NAME_LIST.indexOf(input.routeName) !== -1;
  const isInNotAvailable = input.routeName === NOT_AVAILABLE_ROUTE_NAME;

  // Fuera de las pantallas del módulo no se toca nada: cambiar de empresa desde
  // Documentos no puede sacar a nadie a una pantalla de nóminas que no había pedido.
  if (!isInModule && !isInNotAvailable) return null;

  // Estaba en «no disponible» y la empresa nueva SÍ lo tiene: se le devuelve al módulo.
  // Sin esto se quedaba en `/81241/nominas-no-disponible` diciéndole que A3 no lo
  // soporta, que es justo al revés de lo que pasa.
  if (isInNotAvailable) {
    return input.hasNominaFeature
      ? { name: LIST_ROUTE_NAME, params: companyParams }
      : null;
  }

  // Estaba dentro del módulo y la empresa nueva lo tiene: la vista se recarga sola con
  // el id nuevo, no hay nada que hacer.
  if (input.hasNominaFeature) return null;
  return { name: NOT_AVAILABLE_ROUTE_NAME, params: companyParams };
}
